import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/**
 * App-wide confirm dialog + toast messages, replacing the browser's raw
 * window.confirm() / alert() (2026-09-28).
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Void invoice?', message: '…', confirmLabel: 'Void', danger: true }))) return;
 *
 *   const toast = useToast();
 *   toast('Invoice updated', 'success');   // 'success' | 'error' | 'info'
 */
const FeedbackContext = createContext(null);

const TOAST_MS = { success: 3500, info: 4500, error: 7000 };

export function FeedbackProvider({ children }) {
  const [dialog, setDialog] = useState(null); // { title, message, confirmLabel, cancelLabel, danger, resolve }
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);

  const confirm = useCallback((opts) => {
    const o = typeof opts === 'string' ? { message: opts } : opts;
    return new Promise(resolve => setDialog({
      title: o.title || 'Are you sure?',
      message: o.message || '',
      confirmLabel: o.confirmLabel || 'Confirm',
      cancelLabel: o.cancelLabel || 'Cancel',
      danger: !!o.danger,
      resolve,
    }));
  }, []);

  const dismiss = useCallback(id => setToasts(ts => ts.filter(t => t.id !== id)), []);

  const toast = useCallback((message, variant = 'info') => {
    const id = nextId.current++;
    setToasts(ts => [...ts, { id, message, variant }]);
    setTimeout(() => dismiss(id), TOAST_MS[variant] || TOAST_MS.info);
  }, [dismiss]);

  function close(result) {
    dialog?.resolve(result);
    setDialog(null);
  }

  return (
    <FeedbackContext.Provider value={{ confirm, toast }}>
      {children}
      {dialog && <ConfirmDialog dialog={dialog} onClose={close} />}
      {toasts.length > 0 && (
        <div className="toast-stack" role="status" aria-live="polite">
          {toasts.map(t => (
            <div key={t.id} className={`toast toast-${t.variant}`}>
              <span>{t.message}</span>
              <button className="toast-close" onClick={() => dismiss(t.id)} aria-label="Dismiss">×</button>
            </div>
          ))}
        </div>
      )}
    </FeedbackContext.Provider>
  );
}

function ConfirmDialog({ dialog, onClose }) {
  const cancelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Focus Cancel (not the action) so a stray Enter never confirms a
  // destructive action; Escape cancels. Runs once per dialog, so a toast
  // appearing meanwhile doesn't steal focus back.
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') onCloseRef.current(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="confirm-overlay" onClick={() => onClose(false)}>
      <div className="confirm-dialog" role="alertdialog" aria-modal="true" onClick={e => e.stopPropagation()}>
        <h3 className="confirm-title">{dialog.title}</h3>
        {dialog.message && <p className="confirm-message">{dialog.message}</p>}
        <div className="confirm-actions">
          <button ref={cancelRef} className="btn btn-secondary" onClick={() => onClose(false)}>{dialog.cancelLabel}</button>
          <button className={dialog.danger ? 'btn btn-danger' : 'btn btn-primary'} onClick={() => onClose(true)}>{dialog.confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export function useConfirm() {
  return useContext(FeedbackContext).confirm;
}

export function useToast() {
  return useContext(FeedbackContext).toast;
}
